/**
 * Centralized Query Key Factory
 *
 * This file defines all query keys for the application using @lukemorales/query-key-factory.
 * Each feature/module has its own factory with type-safe key generation.
 *
 * @see https://github.com/lukemorales/query-key-factory
 */
import { createQueryKeyStore } from '@lukemorales/query-key-factory';

import { INTERVALS_BY_ACCOUNT_SCOPE } from '@/hooks/useIntervalsByAccount';
import { PlanningProjectFilters } from '@/modules/Planning/hooks/usePlanningProjects/usePlanningProjects';
import { Project, Resource, Task } from '@/types/types';

/**
 * Query key factories organized by feature/module
 */
export const queryKeys = createQueryKeyStore({
  // Projects
  projects: {
    all: null,
    list: (filters?: {
      status?: string[];
      expired_end_date?: boolean;
      search?: string;
      manager_ids?: (string | number)[];
      category_ids?: (string | number)[];
    }) => ({
      queryKey: [filters],
    }),
    detail: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    taskCompletion: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    economyReport: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    statusChanges: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    pendingIntervalCompletionStates: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    dependencies: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    intervalComments: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
  },

  planningProjects: {
    all: null,
    list: (filters?: PlanningProjectFilters) => ({
      queryKey: [filters],
    }),
    byProject: (projectId: number) => ({
      queryKey: [projectId],
    }),
  },

  partnerPlanningProjects: {
    all: null,
  },

  partnerProjects: {
    all: null,
    detail: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
  },

  // Project Share Links
  projectShareLinks: {
    all: null,
    list: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
  },

  // Plan PDFs (PDF-udskrifter)
  planPdfs: {
    all: null,
    list: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
  },

  // Project Share Link Subscribers
  projectShareLinkSubscribers: {
    list: (projectId: string | number, shareLinkId: string | number) => ({
      queryKey: [projectId, shareLinkId],
    }),
  },

  availabilityCalendars: {
    byAccount: (accountId: string | number) => ({
      queryKey: [accountId],
    }),
  },

  economyForecasts: {
    all: null,
    list: (filters?: { projectId?: Project['id'] }) => ({
      queryKey: [filters],
    }),
  },

  accounts: {
    all: null,
    detail: (accountId: string | number) => ({
      queryKey: [accountId],
    }),
  },

  invitations: {
    all: null,
  },

  seats: {
    current: null,
  },

  companyAccountCategories: {
    all: null,
    detail: (categoryId: string | number) => ({
      queryKey: [categoryId],
    }),
  },

  activityCategories: {
    all: null,
  },

  teams: {
    all: null,
  },

  // Project Milestones
  projectMilestones: {
    all: null,
    list: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    detail: (projectId: string | number, milestoneId: string | number) => ({
      queryKey: [projectId, milestoneId],
    }),
  },

  // Project Closed Days
  projectClosedDays: {
    all: null,
    list: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
  },

  // Project Feedback (Public)
  projectFeedback: {
    all: null,
    detail: (token: string) => ({
      queryKey: [token],
    }),
  },

  // Project Feedbacks (Admin)
  projectFeedbacks: {
    all: null,
    list: (filters?: { projectId?: string | number }) => ({
      queryKey: [filters],
    }),
    detail: (feedbackId: string | number) => ({
      queryKey: [feedbackId],
    }),
    report: (filters?: {
      start_date?: string;
      end_date?: string;
      feedback_type?: string;
      submitted?: string;
    }) => ({
      queryKey: ['report', filters],
    }),
  },

  // Categories
  categories: {
    all: null,
  },

  taskCategories: {
    all: null,
  },

  // Locations
  locations: {
    all: null,
    list: (projectId: number) => ({
      queryKey: [projectId],
    }),
  },

  accountAvailabilities: {
    all: null,
    detail: (accountAvailabilityId: string | number) => ({
      queryKey: [accountAvailabilityId],
    }),
  },

  // Resources
  resources: {
    all: null,
    list: (filters?: {
      projectId?: Project['id'];
      resourceableId?: Resource['resourceable_id'];
      taskId?: Task['id'];
      resourceableType?: Resource['resourceable_type'];
    }) => ({
      queryKey: [filters],
    }),

    detail: (id: string | number) => ({
      queryKey: [id],
    }),
    uniqueByProject: ({ projectId }: { projectId: Project['id'] }) => ({
      queryKey: [projectId],
    }),

    byProject: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
    byTaskId: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
    byIds: (resourceableId: string | number, resourceableType: string) => ({
      queryKey: [resourceableId, resourceableType],
    }),
    byGrouped: (
      groupBy: 'task_id' | 'company_account_category_id',
      filters?: {
        projectId?: Project['id'];
        resourceableId?: string | number;
        resourceableType?: Resource['resourceable_type'];
        taskId?: string | number;
      }
    ) => ({
      queryKey: [groupBy, filters],
    }),
  },

  timesheetReports: {
    project: (projectId?: string | number, startDate?: string, endDate?: string) => ({
      queryKey: ['project', projectId, startDate, endDate],
    }),
    accountUtilization: (accountId?: string | number, startDate?: string, endDate?: string) => ({
      queryKey: ['account-utilization', accountId, startDate, endDate],
    }),
    companyOverview: (
      startDate?: string,
      endDate?: string,
      registerableId?: string | number | undefined,
      registerableType?: 'Account' | 'Partner' | undefined,
      projectId?: string | number | undefined,
      taskId?: string | number | undefined
    ) => ({
      queryKey: [
        'company-overview',
        startDate,
        endDate,
        registerableId,
        registerableType,
        projectId,
        taskId,
      ],
    }),
    task: (taskId: string | number) => ({
      queryKey: ['task', taskId],
    }),
  },

  timesheetEntries: {
    all: null,
    list: (params?: {
      registerableId?: string | number;
      registerableType?: 'Account' | 'Partner';
      startDate?: string;
      endDate?: string;
    }) => ({
      queryKey: [params],
    }),
    detail: (entryId: string | number) => ({
      queryKey: [entryId],
    }),
    byWeek: (params?: {
      startDate?: string;
      endDate?: string;
      registerableId?: string | number | null;
      registerableType?: 'Account' | 'Partner' | null;
      projectId?: string | number | null;
      taskId?: string | number | null;
    }) => ({
      queryKey: [params],
    }),
  },

  // Tasks
  tasks: {
    all: null,
    detail: (id: string | number) => ({
      queryKey: [id],
    }),
    byProject: (projectId: string | number | undefined) => ({
      queryKey: [projectId],
    }),
    infinite: (params?: {
      search?: string;
      sort?: string;
      direction?: 'asc' | 'desc';
      perPage?: number;
      projectIds?: (string | number)[];
    }) => ({
      queryKey: [params],
    }),
    economyReport: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
    expenses: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
    costPrices: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
    salesPrices: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
    hourEstimates: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
    intervalCompletionStates: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
    dependencies: (taskId: string | number) => ({
      queryKey: [taskId],
    }),
  },

  // Intervals
  intervals: {
    all: null,
    byAccount: (scope: INTERVALS_BY_ACCOUNT_SCOPE) => ({
      queryKey: [scope],
    }),
    bookedForWeek: (startDate: string, endDate: string) => ({
      queryKey: [startDate, endDate],
    }),
    byResource: (params: {
      projectId: string | number | null;
      resourceId: string | number;
      intervalableType: string;
      taskId?: string | number;
    }) => ({
      queryKey: [params],
    }),
    byIntervalableIds: (intervalableIds: string) => ({
      queryKey: [intervalableIds],
    }),
    byResourceInterval: (resourceId: string | number) => ({
      queryKey: [resourceId],
    }),
    byAssignment: (projectId: string | number, assignmentId: string | number) => ({
      queryKey: [projectId, assignmentId],
    }),
    completionStates: (intervalId: string | number) => ({
      queryKey: [intervalId],
    }),
    comments: (intervalId: string | number) => ({
      queryKey: [intervalId],
    }),
  },

  // Partners
  partners: {
    all: null,
    detail: (id: string | number) => ({
      queryKey: [id],
    }),
    claimInfo: (token: string) => ({
      queryKey: [token],
    }),
  },

  // Company Search
  companySearch: {
    search: (params: { cvr?: string; name?: string }) => ({
      queryKey: [params],
    }),
  },

  // User Data
  userData: {
    current: null,
  },

  // Admin
  admin: {
    accounts: null,
    accountStatistics: null,
    marketingOptInAccounts: null,
    inactiveAccounts: null,
    companies: null,
    companyAccountCategories: (companyId?: string | number) => ({
      queryKey: [companyId],
    }),
  },

  // Reports
  reports: {
    // One entry per tracker graph (scope + key), e.g. "projects-coverage" —
    // filters go in the key so a category/manager-filtered view caches
    // separately from the unfiltered one.
    trackers: (graphId: string, filters?: Record<string, string | number | undefined>) => ({
      queryKey: [graphId, filters],
    }),
  },

  // AI Usage
  aiUsage: {
    current: null,
    overview: null,
  },

  fileExtractions: {
    kinds: null,
    presets: (kind: string) => ({
      queryKey: [kind],
    }),
  },

  // Project Notes
  projectActivities: {
    all: null,
    list: (projectId: string | number) => ({
      queryKey: [projectId],
    }),
  },

  activityEntries: {
    all: null,
    list: (activityId: string | number) => ({
      queryKey: [activityId],
    }),
  },

  activity: {
    detail: (activityId: string | number) => ({
      queryKey: [activityId],
    }),
  },

  // "Mine aktiviteter" — self-authored-unassigned or delegated-to-me, via ?mine=true.
  myActivities: {
    all: null,
    list: (filters?: object) => ({
      queryKey: [filters],
    }),
    summary: null,
  },

  // Heatmap Observations
  heatmapObservations: {
    list: (filters?: { startDate?: string; endDate?: string }) => ({
      queryKey: [filters],
    }),
  },

  // Dashboard (planner/PM feeds)
  dashboard: {
    stats: null,
    portfolio: null,
    pmActiveProjects: null,
    approvals: (params?: { projectId?: number | string; taskId?: number | string }) => ({
      queryKey: [params],
    }),
    partnerProgress: (params?: { limit?: number }) => ({
      queryKey: [params],
    }),
    upcomingMilestones: (params?: { limit?: number }) => ({
      queryKey: [params],
    }),
    myTasks: (params?: { managerId?: number | string; range?: 'today' | 'week' }) => ({
      queryKey: [params],
    }),
    dueSoonActivities: (params?: { limit?: number }) => ({
      queryKey: [params],
    }),
    myDueSoonActivities: (params?: { limit?: number }) => ({
      queryKey: [params],
    }),
    myAssignedActivities: (params?: { limit?: number }) => ({
      queryKey: [params],
    }),
  },

  // Heatmap Accounts (grouped + utilization summaries)
  heatmapAccounts: {
    list: (params?: {
      groupBy?: string;
      utilization?: string[];
      categoryIds?: (string | number)[];
      startDate?: string;
      endDate?: string;
    }) => ({
      queryKey: [params],
    }),
    bookings: (accountId: string | number, params?: { startDate?: string; endDate?: string }) => ({
      queryKey: [accountId, params],
    }),
  },
});
